export type StoreUser = {
  StoreUserId: string;
  Username: string;
  DisplayName: string;
  Role: "StoreManager" | "Staff" | "Audiologist" | "Support" | string;
  IsActive?: boolean;
  Locations?: StoreLocation[];
  CurrentLocationId?: string | null;
  Permissions?: string[];
  LastLoginAt?: string | null;
};

export type StoreLocation = {
  LocationId: string;
  Name: string;
  Phone?: string | null;
  Address?: string | null;
  IsPrimary?: boolean;
};

export type StoreDashboardMetric = {
  TodayReservations: number;
  NewReservations: number;
  TodayFollowUps: number;
  UpcomingArrivals: number;
  OverdueFollowUps: number;
  PickupOrders: number;
  StoreOrdersInTransit: number;
  StoreOrdersReadyForPickup: number;
  BalanceDueOrders: number;
  NotifiedUnpickedOrders: number;
  UnreadAnnouncements: number;
  MyTasks: number;
  ScheduleCoverageDays?: number | null;
  ScheduleCoverageNeedsAttention?: boolean;
};

export type StoreDashboardReservation = {
  SourceType: "FathersDay" | "ProductTrial" | "Reservation" | string;
  SourceId: string;
  CustomerName: string;
  CustomerPhone: string;
  LocationName: string;
  AppointmentDate: string | null;
  AppointmentTime: string | null;
  Status: string;
  AssignedStoreUserName?: string | null;
};

export type StoreReservationStatus =
  | "New"
  | "Contacted"
  | "Booked"
  | "Confirmed"
  | "Arrived"
  | "Completed"
  | "NoShow"
  | "Cancelled"
  | "FollowUp"
  | "Closed";

export type StoreReservation = {
  SourceType: "FathersDay" | "ProductTrial" | string;
  SourceId: string;
  Title: string;
  CustomerName: string;
  CustomerPhone: string;
  LocationId: string | null;
  LocationName: string;
  AppointmentDate: string | null;
  AppointmentTime: string | null;
  ProductName?: string | null;
  ProductCode?: string | null;
  Note?: string | null;
  Status: StoreReservationStatus | string;
  AssignedStoreUserId?: string | null;
  LastContactedAt?: string | null;
  NextFollowUpAt?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type StoreReservationInteraction = {
  InteractionId: string;
  StoreUserId?: string | null;
  InteractionType: string;
  Body: string | null;
  NextFollowUpAt?: string | null;
  CreatedAt: string;
};

export type StorePaginationMeta = {
  Page: number;
  PerPage: number;
  Total: number;
  TotalPages: number;
};

export type StoreCustomerSummary = {
  CustomerKey: string;
  CustomerId?: string | null;
  MemberId?: string | null;
  Name: string;
  Phone: string;
  Email?: string | null;
  IsMember?: boolean;
  LastLocationId?: string | null;
  LastLocationName?: string | null;
  LatestSourceType?: string | null;
  LatestSourceId?: string | null;
  LastContactAt?: string | null;
  ReservationCount: number;
  OrderCount: number;
  InteractionCount: number;
};

export type StoreCustomerDetail = StoreCustomerSummary & {
  CustomerCode?: string | null;
  MemberId?: string | null;
  Email?: string | null;
  Gender?: string | null;
  Birthday?: string | null;
  AddressCity?: string | null;
  AddressDistrict?: string | null;
  AddressPostalCode?: string | null;
  AddressLine?: string | null;
  PreferredLocationId?: string | null;
  SourceType?: string | null;
  Status?: string | null;
  IsBlacklisted?: boolean;
  Note?: string | null;
  IsMember: boolean;
  IsEmailVerified?: boolean;
  MemberCreatedAt?: string | null;
  Stats?: {
    BalanceAmount: number;
    BalanceDueOrders: number;
    ActiveServiceRecords: number;
    OpenFollowUps: number;
    LastServiceDate?: string | null;
    NextFollowUpAt?: string | null;
  };
};

export type StoreCustomerPayload = {
  LocationId: string;
  Name: string;
  Phone: string;
  Email?: string;
  Gender?: string;
  Birthday?: string;
  AddressCity?: string;
  AddressDistrict?: string;
  AddressPostalCode?: string;
  AddressLine?: string;
  Note?: string;
};

export type StoreCustomerOrder = {
  OrderId: string;
  OrderNumber: string;
  RecipientName: string;
  RecipientPhone: string;
  PickupLocationId?: string | null;
  PickupLocationName?: string | null;
  OrderStatus: string;
  OrderStatusLabel?: string;
  PaymentStatus: string;
  PaymentStatusLabel?: string;
  TotalAmount: number;
  PaidAmount?: number;
  BalanceAmount?: number;
  ProductSummary?: string | null;
  FulfillmentType?: string | null;
  ExpectedPickupDate?: string | null;
  DeliveredAt?: string | null;
  CreatedAt: string;
};

export type StoreCustomerOwnedProduct = {
  CustomerOwnedProductId: string;
  StoreSalesOrderId?: string | null;
  StoreSalesOrderItemId?: string | null;
  OrderNumber?: string | null;
  LocationId?: string | null;
  LocationName?: string | null;
  ProductName: string;
  VariantLabel?: string | null;
  Sku?: string | null;
  SerialNumber?: string | null;
  EarSide?: string | null;
  WarrantyStartDate?: string | null;
  DeliveredAt?: string | null;
  Status: string;
};

export type StoreServiceRecord = {
  ServiceRecordId: string;
  CustomerId?: string | null;
  MemberId?: string | null;
  LocationId: string;
  LocationName?: string | null;
  StoreUserId?: string | null;
  StoreUserName?: string | null;
  ReservationSourceType?: string | null;
  ReservationSourceId?: string | null;
  CustomerName: string;
  CustomerPhone: string;
  VisitDate: string;
  ServiceType: string;
  ServiceTypeLabel?: string;
  Summary?: string | null;
  Recommendation?: string | null;
  NextFollowUpAt?: string | null;
  TrialProductName?: string | null;
  TrialSerialNumber?: string | null;
  TrialEarSide?: string | null;
  Status?: "Active" | "Voided" | string;
  UpdatedByStoreUserId?: string | null;
  VoidedAt?: string | null;
  VoidedByStoreUserId?: string | null;
  VoidReason?: string | null;
  TrialRecords?: StoreProductTrialRecord[];
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type StoreProductTrialRecord = {
  TrialRecordId: string;
  ProductId?: string | null;
  VariantId?: string | null;
  ProductName?: string | null;
  SerialNumber?: string | null;
  EarSide?: string | null;
  StartedAt?: string | null;
  EndedAt?: string | null;
  Result?: string | null;
  CreatedAt?: string | null;
};

export type StoreProductOption = {
  ProductId: string;
  VariantId?: string | null;
  ProductName: string;
  VariantLabel?: string | null;
  DisplayName: string;
  Sku: string;
  Category: string;
  Price: number;
  Stock?: number | null;
  HasVariants: boolean;
  ProductUsageType?: string;
  ProductUsageTypeLabel?: string;
  AllowZeroPriceOrder?: boolean;
};

export type StoreServiceRecordPayload = {
  LocationId: string;
  ProductId?: string;
  VariantId?: string;
  CustomerName: string;
  CustomerPhone: string;
  VisitDate: string;
  ServiceType: string;
  Summary?: string;
  Recommendation?: string;
  NextFollowUpAt?: string;
  TrialProductName?: string;
  TrialSerialNumber?: string;
  TrialEarSide?: string;
  ReservationSourceType?: string;
  ReservationSourceId?: string;
};

export type StoreSalesOrderItem = {
  StoreSalesOrderItemId?: string;
  ProductId: string;
  VariantId?: string | null;
  ProductName: string;
  VariantLabel?: string | null;
  Sku?: string | null;
  UnitPrice: number;
  DealPrice: number;
  Quantity: number;
  LineTotal: number;
  SerialNumber?: string | null;
  EarSide?: string | null;
  WarrantyStartDate?: string | null;
  CreatedAt?: string | null;
};

export type StoreSalesOrderPayment = {
  StoreSalesOrderPaymentId: string;
  ParentPaymentId?: string | null;
  PaymentType: "Deposit" | "Balance" | "Adjustment" | string;
  PaymentTypeLabel?: string;
  PaymentMethod: "Cash" | "CreditCard" | "BankTransfer" | "LinePay" | "Other" | string;
  PaymentMethodLabel?: string;
  Amount: number;
  PaymentStatus?: string;
  PaymentStatusLabel?: string;
  ReconciliationStatus?: string;
  ReconciliationStatusLabel?: string;
  IsAbnormal?: boolean;
  AbnormalNote?: string | null;
  VoidedAt?: string | null;
  VoidReason?: string | null;
  StoreUserId?: string | null;
  StoreUserName?: string | null;
  Note?: string | null;
  ReceivedAt?: string | null;
  CreatedAt?: string | null;
};

export type StoreSalesOrderNotification = {
  StoreSalesOrderNotificationId: string;
  NotificationType: string;
  Method: string;
  RecipientEmail: string;
  Subject: string;
  BodySummary?: string | null;
  Status: "Sent" | "NoAnswer" | "Failed" | "Agreed" | "Rescheduled" | string;
  ErrorMessage?: string | null;
  AdminUserId?: string | null;
  AdminUserName?: string | null;
  StoreUserId?: string | null;
  StoreUserName?: string | null;
  SentAt?: string | null;
  CreatedAt?: string | null;
};

export type StoreSalesOrderDeliveryChecklist = Record<string, { Label: string; Checked: boolean }>;

export type StoreSalesOrder = {
  StoreSalesOrderId: string;
  OrderNumber: string;
  LocationId: string;
  LocationName?: string | null;
  StoreUserId?: string | null;
  StoreUserName?: string | null;
  CustomerId?: string | null;
  MemberId?: string | null;
  CustomerName: string;
  CustomerPhone: string;
  CustomerEmail?: string | null;
  Status: string;
  StatusLabel?: string;
  PaymentStatus: string;
  PaymentStatusLabel?: string;
  FulfillmentType?: "Headquarters" | "StoreStock" | string;
  FulfillmentTypeLabel?: string;
  OrderPurpose?: string | null;
  OrderPurposeLabel?: string | null;
  ZeroPriceReason?: string | null;
  ZeroPriceReasonLabel?: string | null;
  ZeroPriceNote?: string | null;
  DepositAmount: number;
  TotalAmount: number;
  BalanceAmount: number;
  InvoiceType?: string | null;
  InvoiceTypeLabel?: string | null;
  InvoiceStatus?: string | null;
  InvoiceStatusLabel?: string | null;
  InvoiceTaxId?: string | null;
  InvoiceTitle?: string | null;
  InvoiceCarrier?: string | null;
  InvoiceDonationCode?: string | null;
  InvoiceNote?: string | null;
  ExpectedPickupDate?: string | null;
  DeliveredAt?: string | null;
  DeliveredByStoreUserId?: string | null;
  DeliveryNote?: string | null;
  VoidedAt?: string | null;
  VoidedByStoreUserId?: string | null;
  VoidReason?: string | null;
  CustomerNote?: string | null;
  InternalNote?: string | null;
  FirstProductName?: string | null;
  FirstVariantLabel?: string | null;
  ItemCount?: number | null;
  Items?: StoreSalesOrderItem[];
  Payments?: StoreSalesOrderPayment[];
  Notifications?: StoreSalesOrderNotification[];
  DeliveryChecklist?: StoreSalesOrderDeliveryChecklist;
  DeliveryCheckedAt?: string | null;
  DeliveryCheckedByStoreUserId?: string | null;
  StoreReceivedAt?: string | null;
  StoreReceivedByStoreUserId?: string | null;
  StoreReceivedNote?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type StoreSalesOrderPayload = {
  LocationId: string;
  CustomerName: string;
  CustomerPhone: string;
  CustomerEmail?: string;
  PaymentStatus: string;
  FulfillmentType?: string;
  OrderPurpose?: string;
  ZeroPriceReason?: string;
  ZeroPriceNote?: string;
  DeliveryCompleted?: boolean;
  DepositAmount: number;
  InitialPaymentAmount?: number;
  InitialPaymentMethod?: string;
  InitialPaymentNote?: string;
  InvoiceType?: string;
  InvoiceTaxId?: string;
  InvoiceTitle?: string;
  InvoiceCarrier?: string;
  InvoiceDonationCode?: string;
  InvoiceNote?: string;
  ExpectedPickupDate?: string;
  DeliveryNote?: string;
  CustomerNote?: string;
  InternalNote?: string;
  Items: {
    ProductId: string;
    VariantId?: string;
    Quantity: number;
    DealPrice: number;
    SerialNumber?: string;
    EarSide?: string;
    WarrantyStartDate?: string;
  }[];
};

export type StoreSalesOrderPaymentPayload = {
  PaymentType?: "Deposit" | "Balance" | "Adjustment" | string;
  PaymentMethod: "Cash" | "CreditCard" | "BankTransfer" | "LinePay" | "Other" | string;
  Amount: number;
  Note?: string;
  CompleteOrder?: boolean;
  DeliveryChecklist?: Record<string, boolean>;
};

export type StoreSalesOrderNotificationLogPayload = {
  Method: "Phone" | "Line" | "Sms" | "Email" | "Other" | string;
  Status: "Sent" | "NoAnswer" | "Failed" | "Agreed" | "Rescheduled" | string;
  BodySummary: string;
};

export type StoreDashboardFollowUp = {
  FollowUpId: string;
  SourceType?: string | null;
  SourceId?: string | null;
  CustomerName: string;
  CustomerPhone: string;
  Subject: string;
  NextFollowUpAt: string | null;
  Status: string;
  DetailPath?: string | null;
};

export type StoreDashboardOrder = {
  StoreSalesOrderId: string;
  OrderNumber: string;
  CustomerName: string;
  CustomerPhone: string;
  Status: string;
  StatusLabel?: string;
  ExpectedPickupDate?: string | null;
  UpdatedAt?: string | null;
};

export type StoreDashboardTask = {
  Key: string;
  Title: string;
  Description?: string | null;
  Count: number;
  Href: string;
  Tone?: "danger" | "warning" | "info" | "success" | string;
  Priority?: number;
};

export type StoreFollowUp = {
  FollowUpId: string;
  SourceType?: string | null;
  SourceId?: string | null;
  CustomerName: string;
  CustomerPhone: string;
  Subject: string;
  NextFollowUpAt: string | null;
  Status: "Open" | "Completed" | string;
  StatusLabel: string;
  StoreUserName?: string | null;
  CompletedAt?: string | null;
  CompletedNote?: string | null;
  PostponedAt?: string | null;
  PostponedNote?: string | null;
  CreatedAt?: string | null;
  IsOverdue: boolean;
  IsToday: boolean;
  DetailPath?: string | null;
};

export type StoreFollowUpCounts = {
  Total: number;
  Overdue: number;
  Today: number;
  Upcoming: number;
  Completed: number;
};

export type StoreDashboard = {
  Metrics: StoreDashboardMetric;
  Tasks?: StoreDashboardTask[];
  TodayReservations: StoreDashboardReservation[];
  NewReservations: StoreDashboardReservation[];
  FollowUps: StoreDashboardFollowUp[];
  ArrivalOrders?: StoreDashboardOrder[];
};

export type StoreAnnouncement = {
  StoreAnnouncementId: string;
  Title: string;
  Category: "Announcement" | "SOP" | "Promotion" | "Policy" | string;
  CategoryLabel: string;
  Priority: "Normal" | "High" | string;
  PriorityLabel: string;
  AudienceType: "All" | "Locations" | string;
  Body: string;
  IsPinned: boolean;
  IsRead: boolean;
  ReadAt?: string | null;
  PublishedAt?: string | null;
  ExpiresAt?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

export type StoreBookingLocation = {
  LocationId: string;
  Name: string;
  Phone?: string | null;
  Address?: string | null;
};

export type StoreBookingSlot = {
  Time: string;
  Capacity: number;
  Booked: number;
  Available: boolean;
};

export type StoreBookingAvailability = {
  BookingAvailabilityId?: string | null;
  LocationId: string;
  LocationName: string;
  AppointmentDate: string;
  IsOpen: boolean;
  Slots: StoreBookingSlot[];
  RawSlots: string[];
  SlotCapacity: number;
  IsNationalHoliday: boolean;
  Note?: string | null;
  UpdatedAt?: string | null;
};

export type StoreBookingAvailabilityPayload = {
  LocationId: string;
  AppointmentDate: string;
  IsOpen: boolean;
  Slots: string[];
  SlotCapacity: number;
  IsNationalHoliday?: boolean;
  Note?: string;
};

export type StoreReportMetrics = {
  OrderCount: number;
  TotalAmount: number;
  PaidAmount: number;
  PaymentReceived: number;
  BalanceAmount: number;
  AverageOrderAmount: number;
  CompletedOrders: number;
  CancelledOrders: number;
  BalanceDueOrders: number;
  DeliverableOrders: number;
  CompletionRate: number;
  ServiceRecordCount: number;
  FollowUpCreated: number;
  FollowUpCompleted: number;
  FollowUpCompletionRate: number;
};

export type StoreReportDailyOrder = {
  Date: string;
  OrderCount: number;
  TotalAmount: number;
  PaidAmount: number;
};

export type StoreReportAmountRow = {
  Key: string;
  Label: string;
  Count: number;
  TotalAmount?: number;
  Amount?: number;
};

export type StoreReportCountRow = {
  Key: string;
  Label: string;
  Count: number;
};

export type StoreReport = {
  LocationId?: string;
  DateFrom: string;
  DateTo: string;
  Metrics: StoreReportMetrics;
  DailyOrders: StoreReportDailyOrder[];
  OrderStatuses: StoreReportAmountRow[];
  PaymentMethods: StoreReportAmountRow[];
  ServiceTypes: StoreReportCountRow[];
  FollowUpStatus: StoreReportCountRow[];
};

type ApiResponse<T> = T & {
  ok: boolean;
  message?: string;
  errors?: Record<string, string>;
  CsrfToken?: string;
};

export class StoreApiError extends Error {
  errors: Record<string, string>;
  status: number;

  constructor(message: string, errors: Record<string, string> = {}, status = 0) {
    super(message);
    this.name = "StoreApiError";
    this.errors = errors;
    this.status = status;
  }
}

const csrfKey = "fulling_store_csrf";

export function getStoreCsrfToken() {
  return sessionStorage.getItem(csrfKey) || "";
}

function setStoreCsrfToken(token?: string) {
  if (token) {
    sessionStorage.setItem(csrfKey, token);
  }
}

export function clearStoreCsrfToken() {
  sessionStorage.removeItem(csrfKey);
}

type StoreRequestOptions = {
  body?: string;
  headers?: Record<string, string>;
  method?: string;
};

async function request<T>(path: string, options: StoreRequestOptions = {}) {
  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(getStoreCsrfToken() ? { "X-CSRF-Token": getStoreCsrfToken() } : {}),
      ...(options.headers || {}),
    },
  });

  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (data && data.CsrfToken) {
    setStoreCsrfToken(data.CsrfToken);
  }
  if (!response.ok || data.ok === false) {
    throw new StoreApiError(data.message || "操作失敗，請稍後再試。", data.errors || {}, response.status);
  }
  return data;
}

export async function fetchStoreCsrf() {
  const data = await request<{ CsrfToken: string }>("/api/store/csrf");
  setStoreCsrfToken(data.CsrfToken);
  return data.CsrfToken;
}

export async function loginStore(Username: string, Password: string) {
  if (!getStoreCsrfToken()) {
    await fetchStoreCsrf();
  }
  return request<{ User: StoreUser; CsrfToken: string }>("/api/store/login", {
    method: "POST",
    body: JSON.stringify({ Username, Password }),
  });
}

export async function getCurrentStoreUser() {
  return request<{ User: StoreUser; CsrfToken: string }>("/api/store/me");
}

export async function logoutStore() {
  const result = await request<Record<string, never>>("/api/store/logout", { method: "POST" });
  clearStoreCsrfToken();
  return result;
}

export async function getStoreDashboard(LocationId?: string | null) {
  const suffix = LocationId ? `?LocationId=${encodeURIComponent(LocationId)}` : "";
  return request<StoreDashboard>(`/api/store/dashboard${suffix}`);
}

export type StoreGoogleCalendarSetting = {
  StoreGoogleCalendarSettingId: string | null;
  LocationId: string;
  LocationName: string;
  GoogleEmail: string;
  CalendarId: string;
  CalendarName: string | null;
  IsEnabled: boolean;
  LastCheckedAt: string | null;
  LastErrorMessage: string | null;
  OAuthGoogleEmail: string | null;
  OAuthScope: string | null;
  TokenExpiresAt: string | null;
  AuthorizedAt: string | null;
  OAuthStatus: "NotConnected" | "Connected" | "Error" | string;
  OAuthLastErrorMessage: string | null;
};

export async function getStoreGoogleCalendar(LocationId?: string | null) {
  const suffix = LocationId ? `?LocationId=${encodeURIComponent(LocationId)}` : "";
  return request<{ Calendar: StoreGoogleCalendarSetting | null; LocationId?: string }>(`/api/store/google-calendar${suffix}`);
}

export async function updateStoreGoogleCalendar(payload: { LocationId?: string | null; GoogleEmail: string; CalendarId?: string | null; CalendarName?: string | null }) {
  return request<{ message?: string; Calendar: StoreGoogleCalendarSetting }>("/api/store/google-calendar", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function startStoreGoogleCalendarOAuth(LocationId?: string | null) {
  return request<{ AuthorizationUrl: string }>("/api/store/google-calendar/oauth/start", {
    method: "POST",
    body: JSON.stringify({ LocationId }),
  });
}

export type StoreGoogleCalendarEvent = {
  EventId: string;
  Summary: string;
  Description: string | null;
  Location: string | null;
  ColorId: string | null;
  Attendees: string[];
  HtmlLink: string | null;
  Status: string | null;
  StartAt: string | null;
  EndAt: string | null;
  IsAllDay: boolean;
  ReminderUseDefault: boolean;
  ReminderEnabled: boolean;
  ReminderMethod: "popup" | "email";
  ReminderMinutes: number;
  RecurrenceType: "None" | "Daily" | "Weekly" | "Monthly" | "Yearly";
  RecurrenceUntil: string | null;
  Transparency: "opaque" | "transparent";
  Visibility: "default" | "public" | "private" | "confidential";
  GuestsCanInviteOthers: boolean;
  GuestsCanModify: boolean;
  GuestsCanSeeOtherGuests: boolean;
  HasGoogleMeet: boolean;
  HangoutLink: string | null;
  UpdatedAt: string | null;
};

export type StoreGoogleCalendarEventPayload = {
  LocationId?: string | null;
  Summary: string;
  Description?: string | null;
  Location?: string | null;
  StartAt: string;
  EndAt: string;
  IsAllDay: boolean;
  ColorId?: string | null;
  Attendees?: string;
  ReminderUseDefault?: boolean;
  ReminderEnabled?: boolean;
  ReminderMethod?: "popup" | "email";
  ReminderMinutes?: number;
  RecurrenceType?: "None" | "Daily" | "Weekly" | "Monthly" | "Yearly";
  RecurrenceUntil?: string | null;
  Transparency?: "opaque" | "transparent";
  Visibility?: "default" | "public" | "private" | "confidential";
  GuestsCanInviteOthers?: boolean;
  GuestsCanModify?: boolean;
  GuestsCanSeeOtherGuests?: boolean;
  CreateGoogleMeet?: boolean;
};

function storeGoogleCalendarEventQuery(params: { LocationId?: string | null; TimeMin?: string; TimeMax?: string }) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.TimeMin) query.set("TimeMin", params.TimeMin);
  if (params.TimeMax) query.set("TimeMax", params.TimeMax);
  return query.toString();
}

export async function listStoreGoogleCalendarEvents(params: { LocationId?: string | null; TimeMin?: string; TimeMax?: string }) {
  return request<{ Events: StoreGoogleCalendarEvent[] }>(`/api/store/google-calendar/events?${storeGoogleCalendarEventQuery(params)}`);
}

export async function createStoreGoogleCalendarEvent(payload: StoreGoogleCalendarEventPayload) {
  return request<{ message?: string; Event: StoreGoogleCalendarEvent }>("/api/store/google-calendar/events", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateStoreGoogleCalendarEvent(eventId: string, payload: StoreGoogleCalendarEventPayload) {
  return request<{ message?: string; Event: StoreGoogleCalendarEvent }>(`/api/store/google-calendar/events/${encodeURIComponent(eventId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteStoreGoogleCalendarEvent(eventId: string, LocationId?: string | null) {
  const suffix = storeGoogleCalendarEventQuery({ LocationId });
  return request<{ message?: string }>(`/api/store/google-calendar/events/${encodeURIComponent(eventId)}${suffix ? `?${suffix}` : ""}`, {
    method: "DELETE",
  });
}

export async function getStoreReports(params: { LocationId?: string | null; DateFrom?: string; DateTo?: string }) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<StoreReport>(`/api/store/reports${suffix}`);
}

export async function exportStoreReports(params: { LocationId?: string | null; DateFrom?: string; DateTo?: string }) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(`/api/store/reports/export${suffix}`, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ...(getStoreCsrfToken() ? { "X-CSRF-Token": getStoreCsrfToken() } : {}),
    },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new StoreApiError(data?.message || "匯出失敗，請稍後再試。", {}, response.status);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const fallbackName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = encodedName ? decodeURIComponent(encodedName) : fallbackName || `StoreReport-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function listStoreFollowUps(params: {
  LocationId?: string | null;
  Status?: string;
  Keyword?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
}) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    FollowUps: StoreFollowUp[];
    Counts: StoreFollowUpCounts;
    Pagination: StorePaginationMeta;
    StatusOptions: { Key: string; Label: string }[];
  }>(`/api/store/follow-ups${suffix}`);
}

export async function completeStoreFollowUp(FollowUpId: string, payload: { Note?: string }) {
  return request<{ FollowUp: StoreFollowUp; message?: string }>(`/api/store/follow-ups/${encodeURIComponent(FollowUpId)}/complete`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function postponeStoreFollowUp(FollowUpId: string, payload: { NextFollowUpAt: string; Note?: string }) {
  return request<{ FollowUp: StoreFollowUp; message?: string }>(`/api/store/follow-ups/${encodeURIComponent(FollowUpId)}/postpone`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listStoreReservations(params: {
  LocationId?: string | null;
  Status?: string;
  SourceType?: string;
  Keyword?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
}) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.Status) query.set("Status", params.Status);
  if (params.SourceType) query.set("SourceType", params.SourceType);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Reservations: StoreReservation[];
    Counts: Record<string, number>;
    Pagination: StorePaginationMeta;
  }>(`/api/store/reservations${suffix}`);
}

export async function getStoreReservation(SourceType: string, SourceId: string) {
  return request<{ Reservation: StoreReservation; Interactions: StoreReservationInteraction[] }>(
    `/api/store/reservations/${encodeURIComponent(SourceType)}/${encodeURIComponent(SourceId)}`,
  );
}

export async function updateStoreReservationStatus(SourceType: string, SourceId: string, Status: StoreReservationStatus) {
  return request<{ Reservation: StoreReservation; message?: string }>(
    `/api/store/reservations/${encodeURIComponent(SourceType)}/${encodeURIComponent(SourceId)}/status`,
    { method: "POST", body: JSON.stringify({ Status }) },
  );
}

export async function addStoreReservationNote(SourceType: string, SourceId: string, payload: { Body?: string; NextFollowUpAt?: string }) {
  return request<{ Reservation: StoreReservation; Interaction: StoreReservationInteraction | null; message?: string }>(
    `/api/store/reservations/${encodeURIComponent(SourceType)}/${encodeURIComponent(SourceId)}/note`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export async function searchStoreCustomers(params: { LocationId?: string | null; Keyword?: string; Page?: number; PerPage?: number }) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{ Customers: StoreCustomerSummary[]; Pagination: StorePaginationMeta }>(`/api/store/customers/search${suffix}`);
}

export async function getStoreCustomer(CustomerKey: string) {
  return request<{
    Customer: StoreCustomerDetail;
    Reservations: StoreReservation[];
    Orders: StoreCustomerOrder[];
    OwnedProducts: StoreCustomerOwnedProduct[];
    ServiceRecords: StoreServiceRecord[];
    Interactions: StoreReservationInteraction[];
  }>(`/api/store/customers/${encodeURIComponent(CustomerKey)}`);
}

export async function createStoreCustomer(payload: StoreCustomerPayload) {
  return request<{ Customer: StoreCustomerDetail; WasExisting?: boolean; message?: string }>("/api/store/customers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateStoreCustomer(CustomerKey: string, payload: StoreCustomerPayload) {
  return request<{ Customer: StoreCustomerDetail; message?: string }>(`/api/store/customers/${encodeURIComponent(CustomerKey)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function addStoreCustomerInteraction(CustomerKey: string, payload: { Body?: string; NextFollowUpAt?: string; LocationId?: string | null }) {
  return request<{ Customer: StoreCustomerDetail; Interaction: StoreReservationInteraction | null; message?: string }>(
    `/api/store/customers/${encodeURIComponent(CustomerKey)}/interactions`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export async function listStoreServiceRecords(params: {
  LocationId?: string | null;
  Keyword?: string;
  ServiceType?: string;
  RecordStatus?: string;
  DateFrom?: string;
  DateTo?: string;
  IncludeVoided?: boolean;
  Page?: number;
  PerPage?: number;
}) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.ServiceType) query.set("ServiceType", params.ServiceType);
  if (params.RecordStatus) query.set("RecordStatus", params.RecordStatus);
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  if (params.IncludeVoided) query.set("IncludeVoided", "1");
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    ServiceRecords: StoreServiceRecord[];
    Counts: Record<string, number>;
    Pagination: StorePaginationMeta;
    ServiceTypeOptions: { Key: string; Label: string }[];
  }>(`/api/store/service-records${suffix}`);
}

export async function getStoreServiceRecord(ServiceRecordId: string) {
  return request<{ ServiceRecord: StoreServiceRecord }>(`/api/store/service-records/${encodeURIComponent(ServiceRecordId)}`);
}

export async function createStoreServiceRecord(payload: StoreServiceRecordPayload) {
  return request<{ ServiceRecord: StoreServiceRecord; message?: string }>("/api/store/service-records", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateStoreServiceRecord(ServiceRecordId: string, payload: StoreServiceRecordPayload) {
  return request<{ ServiceRecord: StoreServiceRecord; message?: string }>(`/api/store/service-records/${encodeURIComponent(ServiceRecordId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function voidStoreServiceRecord(ServiceRecordId: string, VoidReason: string) {
  return request<{ ServiceRecord: StoreServiceRecord; message?: string }>(`/api/store/service-records/${encodeURIComponent(ServiceRecordId)}/void`, {
    method: "POST",
    body: JSON.stringify({ VoidReason }),
  });
}

export async function searchStoreServiceProducts(Keyword: string, Limit = 12) {
  const query = new URLSearchParams();
  if (Keyword) query.set("Keyword", Keyword);
  query.set("Limit", String(Limit));
  return request<{ Products: StoreProductOption[] }>(`/api/store/service-records/products/search?${query.toString()}`);
}

export async function listStoreOrders(params: {
  LocationId?: string | null;
  Keyword?: string;
  Status?: string;
  PaymentStatus?: string;
  DateFrom?: string;
  DateTo?: string;
  Page?: number;
  PerPage?: number;
}) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Status) query.set("Status", params.Status);
  if (params.PaymentStatus) query.set("PaymentStatus", params.PaymentStatus);
  if (params.DateFrom) query.set("DateFrom", params.DateFrom);
  if (params.DateTo) query.set("DateTo", params.DateTo);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Orders: StoreSalesOrder[];
    Counts: Record<string, number>;
    Pagination: StorePaginationMeta;
    StatusOptions: { Key: string; Label: string }[];
    PaymentStatusOptions: { Key: string; Label: string }[];
    FulfillmentTypeOptions?: { Key: string; Label: string }[];
  }>(`/api/store/orders${suffix}`);
}

export async function getStoreOrder(StoreSalesOrderId: string) {
  return request<{ Order: StoreSalesOrder }>(`/api/store/orders/${encodeURIComponent(StoreSalesOrderId)}`);
}

export async function createStoreOrder(payload: StoreSalesOrderPayload) {
  return request<{ Order: StoreSalesOrder; message?: string }>("/api/store/orders", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateStoreOrder(StoreSalesOrderId: string, payload: StoreSalesOrderPayload) {
  return request<{ Order: StoreSalesOrder; message?: string }>(`/api/store/orders/${encodeURIComponent(StoreSalesOrderId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function voidStoreOrder(StoreSalesOrderId: string, VoidReason: string) {
  return request<{ Order: StoreSalesOrder; message?: string }>(`/api/store/orders/${encodeURIComponent(StoreSalesOrderId)}/void`, {
    method: "POST",
    body: JSON.stringify({ VoidReason }),
  });
}

export async function createStoreOrderPayment(StoreSalesOrderId: string, payload: StoreSalesOrderPaymentPayload) {
  return request<{ Order: StoreSalesOrder; message?: string }>(`/api/store/orders/${encodeURIComponent(StoreSalesOrderId)}/payments`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function sendStoreOrderPickupNotification(StoreSalesOrderId: string) {
  return request<{ Order: StoreSalesOrder; message?: string }>(`/api/store/orders/${encodeURIComponent(StoreSalesOrderId)}/pickup-notification`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function confirmStoreOrderReceipt(StoreSalesOrderId: string, payload: { Note?: string } = {}) {
  return request<{ Order: StoreSalesOrder; message?: string }>(`/api/store/orders/${encodeURIComponent(StoreSalesOrderId)}/store-receipt`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function addStoreOrderNotificationLog(StoreSalesOrderId: string, payload: StoreSalesOrderNotificationLogPayload) {
  return request<{ Order: StoreSalesOrder; message?: string }>(`/api/store/orders/${encodeURIComponent(StoreSalesOrderId)}/notifications`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function searchStoreOrderProducts(Keyword: string, Limit = 12) {
  const query = new URLSearchParams();
  if (Keyword) query.set("Keyword", Keyword);
  query.set("Limit", String(Limit));
  return request<{ Products: StoreProductOption[] }>(`/api/store/orders/products/search?${query.toString()}`);
}

export async function listStoreAnnouncements(params: {
  LocationId?: string | null;
  Category?: string;
  Status?: string;
  Keyword?: string;
  Page?: number;
  PerPage?: number;
}) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.Category) query.set("Category", params.Category);
  if (params.Status) query.set("Status", params.Status);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Items: StoreAnnouncement[];
    Counts: Record<string, number>;
    Pagination: StorePaginationMeta;
    CategoryOptions: { Key: string; Label: string }[];
  }>(`/api/store/announcements${suffix}`);
}

export async function getStoreAnnouncement(StoreAnnouncementId: string, LocationId?: string | null) {
  const suffix = LocationId ? `?LocationId=${encodeURIComponent(LocationId)}` : "";
  return request<{ Item: StoreAnnouncement }>(`/api/store/announcements/${encodeURIComponent(StoreAnnouncementId)}${suffix}`);
}

export async function markStoreAnnouncementRead(StoreAnnouncementId: string, LocationId?: string | null) {
  return request<{ Item: StoreAnnouncement; message?: string }>(`/api/store/announcements/${encodeURIComponent(StoreAnnouncementId)}/read`, {
    method: "POST",
    body: JSON.stringify({ LocationId }),
  });
}

export async function listStoreBookingAvailability(params: { LocationId?: string; StartDate?: string; EndDate?: string }) {
  const query = new URLSearchParams();
  if (params.LocationId) query.set("LocationId", params.LocationId);
  if (params.StartDate) query.set("StartDate", params.StartDate);
  if (params.EndDate) query.set("EndDate", params.EndDate);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<{
    Locations: StoreBookingLocation[];
    SelectedLocationId: string | null;
    Items: StoreBookingAvailability[];
    BookingPublicLookaheadDays?: number;
    Coverage?: {
      ConfiguredThrough: string | null;
      DaysRemaining: number | null;
      WarningThresholdDays: number;
    };
  }>(`/api/store/booking-availability${suffix}`);
}

export async function saveStoreBookingAvailability(payload: StoreBookingAvailabilityPayload) {
  return request<{ Item: StoreBookingAvailability }>("/api/store/booking-availability", {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function bulkGenerateStoreBookingAvailability(payload: {
  LocationId: string;
  StartDate: string;
  EndDate: string;
  Weekdays: number[];
  Slots: string[];
  SlotCapacity: number;
  Overwrite: boolean;
}) {
  return request<{ Created: number; Updated: number; Skipped: number }>("/api/store/booking-availability/bulk-generate", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
